import { apiClient } from './apiClient';
import type { SkillRatingScale } from './types/jobEnd.types';

// ============ Types ============

// Job End item on the dashboard
export interface DashboardJobEnd {
  id: number;
  title: string;
  appraiseeId: number;
  appraiseeName: string;
  collatorId: number;
  collatorName: string;
  dueDate: string;
  status: string;
  projectName: string;
  role: string;
  delegationStatus: string;
  isOriginalCollator: boolean;
}

export interface DashboardSeniorFeedback {
  id: number;
  jobEndId: number;
  jobEndTitle: string;
  reviewerId: number;
  reviewerName: string;
  requestedById: number;
  requestedByName: string;
  dueDate: string;
  status: string;
  appraiseeId: number;
  appraiseeName: string;
  projectName: string;
}

export interface DashboardQualtricsForm {
  id: number;
  formName: string;
  assignedToId: number;
  assignedToName: string;
  title: string;
  dueDate: string;
  status: string;
  formType: string;
  link: string;
  projectName: string;
}

// Past evaluation (delivered Job End) on the dashboard
export interface DashboardPastEvaluation {
  id: number;
  projectName: string;
  projectCode: string;
  assignmentStartDate: string;
  assignmentEndDate: string | null;
  tenureInGrade: string | null;
  overallRating: string | null;
}

// Appraisal item on the dashboard
export interface DashboardAppraisal {
  id: number;
  cycleName: string;
  cycleEndDate: string;
  tenureInGrade: string | null;
  rating: SkillRatingScale | null;
}

// My Details section on the dashboard
export interface MyDetailsDto {
  name: string;
  billingGrade: string | null;
  tenureInGrade: string | null;
  lastAppraisalRating: string | null;
  staffingWeeks: number | null;
}

// Activity section: job ends involving the viewed employee in any role
export interface JobEndActivity {
  id: number;
  projectName: string;
  dueDate: string;
  role: string;
  status: string;
}

// Appraisals section DTOs

export interface DashboardMyAppraisal {
  id: number;
  appraiseeFirstName: string;
  appraiseeLastName: string;
  selfAssessmentDate: string | null;
  status: string;
}

export interface DashboardCollationItem {
  id: number;
  appraiseeFirstName: string;
  appraiseeLastName: string;
  collationDeadline: string | null;
  earliestDeliveryDate: string | null;
  status: string;
}

export interface DashboardTcAppraisee {
  appraisalId: number;
  appraiseeFirstName: string;
  appraiseeLastName: string;
  tenureInGrade: string | null;
  status: string;
}

export interface DashboardTalentCommittee {
  tcId: number;
  tcName: string;
  plannedDate: string | null;
  appraisees: DashboardTcAppraisee[];
}

export interface DashboardInterview {
  interviewCandidateId: number;
  intervieweeName: string;
  intervieweeTenureInGrade: string;
  appraisalId: number;
  appraiseeFirstName: string;
  appraiseeLastName: string;
  completed: boolean;
}

export interface DashboardHappinessSurveyItem {
  id: number;
  status: string;
  dueDate: string;
  qualtricsLink: string | null;
}

export interface DashboardHappinessSurvey {
  completionRate: number | null;
  surveys: DashboardHappinessSurveyItem[];
}

// Main dashboard response containing all items
export interface DashboardDto {
  jobEnds: DashboardJobEnd[];
  seniorFeedback: DashboardSeniorFeedback[];
  qualtricsForm: DashboardQualtricsForm[];
  pastEvaluations: DashboardPastEvaluation[];
  details: MyDetailsDto | null;
  jobEndActivity?: JobEndActivity[];
  appraisals: DashboardAppraisal[];
  myAppraisal: DashboardMyAppraisal | null;
  collation: DashboardCollationItem[];
  talentCommittees: DashboardTalentCommittee[];
  plannedInterviews: DashboardInterview[];
  happinessSurvey?: DashboardHappinessSurvey | null;
}

export const dashboardApi = {
  getMyItems: async (): Promise<DashboardDto> => {
    const response = await apiClient.get<DashboardDto>('/dashboard/my-items');
    return response.data;
  },
};

export default dashboardApi;
